import { FC, useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import { isNil } from 'lodash'
import { FormikProvider, useFormik } from 'formik'
import { number as yupNumber, object, string as yupString } from 'yup'

import { useAppDispatch } from '@/utils/typeHelpers'
import { useReselector } from '@/utils/sharedHooks'
import {
  updateUserTaxEstimates,
  UserTaxEstimate,
} from '../userTaxEstimates.slice'
import { shortNames } from '@/constants/statesShortNamesConstants'
import {
  centsToDollars,
  dollarsToCents,
  formatCurrency,
} from '@/utils/currencyHelpers'
import { DATE_FORMATS } from '@/utils/dateHelpers'
import { UploadDocumentType } from '@/constants/businessConstants'
import { useAnalyticsTrack } from '@/features/amplitude'
import { splitTaxYearQuarter } from '../helpers'
import { markUserActionItemCompleteIfExists } from '@/features/user/dashboard/lib/service'
import { UserActionItemActionItemIdentifiers } from '@/features/user/dashboard/user-action-items/lib/userActionItems.slice'
import { getUserDocumentById } from '@/features/user/your-practice/documents/lib/userDocuments.selector'
import { fetchUserDocuments } from '@/features/user/your-practice/documents/lib/userDocuments.slice'
import { useDocumentUpload } from '@/components/documents/upload/hooks/useDocumentUpload'
import { UserDocumentCategoryIdentifier } from '@/features/admin/user-document-categories/userDocumentCategory.constants'
import { selectUserDocumentCategoryByInternalName } from '@/features/admin/user-document-categories/userDocumentCategories.selectors'
import { fetchUserDocumentCategoriesIfNeeded } from '@/features/admin/user-document-categories/userDocumentCategories.slice'

import { Button, Form, Dialog, Text, Link, DocumentUploadDialog } from '@/ui'
import { TypographyStyles, TextColors } from '@/styles'

// TODO: Migrate to V2
import {
  FormikRadioToggleButton,
  getFieldName,
  makeDateSchema,
} from '@/componentsV1/BaseComponents'

interface Props {
  open: boolean
  close: () => void
  estimate: UserTaxEstimate
  /** Opt-in: callers with a derived figure to refresh pass this. */
  onSaved?: () => void
  /**
   * What the caller is telling the member to pay, when that is not the row's
   * own figure. A safe-harbor catch-up spans several quarters, so the row holds
   * one quarter and the screen holds the total.
   */
  amountInCentsOverride?: number
}

const renderTitle = (estimate: UserTaxEstimate) => {
  const isFederal = estimate.type === 'federal'
  const { quarter, year } = splitTaxYearQuarter(estimate.taxQuarter)

  const estimateTypes = isFederal
    ? 'Federal'
    : estimate.filingState
      ? `${shortNames[estimate.filingState]}`
      : '[State not provided]'

  return `Update Tax Payment - ${estimateTypes}, Q${quarter} ${year}`
}

const validationSchema = object({
  paymentAmountInDollars: yupNumber()
    .min(0.0)
    .when('paymentSubmitted', ([paymentSubmitted]) =>
      paymentSubmitted === 'yes'
        ? yupNumber().min(0.0).required()
        : yupNumber().notRequired()
    ),
  paymentDate: yupString().when(
    'paymentSubmitted',
    ([paymentSubmitted], schema) =>
      paymentSubmitted === 'yes'
        ? makeDateSchema({ field: 'payment date' })
        : schema
  ),
  // value is manually set via formik's api
  paymentSubmitted: yupString().required(),
})

// Form value state is managed by both Formik and React.useState
export const UpdateQuarterlyTaxEstimateDialog: FC<Props> = ({
  close,
  estimate,
  open,
  onSaved,
  amountInCentsOverride,
}) => {
  const { quarter, year } = splitTaxYearQuarter(estimate.taxQuarter)
  const [loading, setLoading] = useState(false)
  const amountToShowInCents = amountInCentsOverride ?? estimate.estimateInCents
  const document = useReselector(getUserDocumentById, estimate.userDocumentId)
  const dispatch = useAppDispatch()
  const track = useAnalyticsTrack()

  const categoryIdentifier = useMemo(() => {
    switch (quarter) {
      case '1':
        return UserDocumentCategoryIdentifier.q1Payment
      case '2':
        return UserDocumentCategoryIdentifier.q2Payment
      case '3':
        return UserDocumentCategoryIdentifier.q3Payment
      case '4':
        return UserDocumentCategoryIdentifier.q4Payment
      default:
        return UserDocumentCategoryIdentifier.qtePayment
    }
  }, [quarter])

  const category = useReselector(
    selectUserDocumentCategoryByInternalName,
    categoryIdentifier
  )

  const [uploadedFile, setUploadedFile] = useState<{
    title: string
    url: string
    id: number | undefined
  }>({
    title: document?.fileDescription || '',
    url: document?.signedUrl || '',
    id: document?.id,
  })

  useEffect(() => {
    dispatch(fetchUserDocuments())
    dispatch(fetchUserDocumentCategoriesIfNeeded())
  }, [dispatch])

  useEffect(() => {
    if (document?.id) {
      setUploadedFile({
        title: document.fileDescription || '',
        url: document.signedUrl || '',
        id: document.id,
      })
    }
  }, [document?.fileDescription, document?.id, document?.signedUrl])

  const { uploadDocuments, isUploading } = useDocumentUpload({
    documentType: UploadDocumentType.TAX,
    year,
    userDocumentCategoryId: category?.id || null,
    onUploadSuccess: async (uploadedDocs) => {
      if (uploadedDocs.length > 0) {
        const firstDoc = uploadedDocs[0]
        setUploadedFile({
          title: firstDoc.title,
          url: firstDoc.url,
          id: firstDoc.id,
        })
        await dispatch(fetchUserDocuments())
      }
    },
    additionalTrackingProperties: {
      tax_year: Number(year),
      tax_quarter: quarter,
    },
  })

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      paymentSubmitted: isNil(estimate.quarterPaid)
        ? undefined
        : estimate.quarterPaid
          ? 'yes'
          : 'no',
      paymentAmountInDollars: estimate.amountPaidInCents
        ? centsToDollars(estimate.amountPaidInCents)
        : '',
      paymentDate: estimate.paidAt
        ? moment(estimate.paidAt).format(DATE_FORMATS.INPUT)
        : '',
    },
    validationSchema,
    onSubmit: async (values) => {
      if (!quarter || !year) {
        return
      }

      setLoading(true)
      let quarterPaid
      let paidAt
      let amountPaidInCents

      if (values.paymentSubmitted === 'no') {
        // Reset paidAt and amount in case they previously had values
        quarterPaid = false
        paidAt = null
        amountPaidInCents = null
      } else {
        quarterPaid = true
        paidAt = moment(values.paymentDate, DATE_FORMATS.INPUT).valueOf()
        amountPaidInCents = dollarsToCents(values.paymentAmountInDollars)
      }

      await updateUserTaxEstimates(estimate.id, {
        quarterPaid,
        paidAt,
        amountPaidInCents,
        userDocumentId: uploadedFile.id,
      })(dispatch)

      // Before the action-item round trip below, so a derived figure does not
      // wait on an unrelated request to refresh.
      onSaved?.()

      await markUserActionItemCompleteIfExists(
        UserActionItemActionItemIdentifiers.submitQTEPayment({
          taxQuarter: quarter,
          taxYear: year,
        }),
        (event, properties) => track(event, properties)
      )

      track('updated tax estimate payment', {
        year,
        quarter,
        quarterPaid,
        paidAt: moment(values.paymentDate, DATE_FORMATS.INPUT).toString(),
        amountPaidInCents,
        uploadedFileId: uploadedFile.id || null,
      })

      setLoading(false)
      close()
    },
  })

  const paymentSubmitted = isNil(formik.values.paymentSubmitted)
    ? undefined
    : formik.values.paymentSubmitted

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && close()}>
      <Dialog.Content className="sm:max-w-xl">
        <Dialog.Title>{renderTitle(estimate)}</Dialog.Title>

        <FormikProvider value={formik}>
          <div className="space-y-6">
            {(amountInCentsOverride ||
              estimate.estimateInCents ||
              estimate.status !== 'active') && (
              <div>
                <Text color={TextColors.MUTED} className="mb-4">
                  {amountInCentsOverride
                    ? 'Total amount to be paid'
                    : 'Estimated amount to be paid'}
                </Text>
                <Text variant={TypographyStyles.H1} className="mt-4">
                  {amountToShowInCents !== null
                    ? formatCurrency(centsToDollars(amountToShowInCents))
                    : 'Awaiting Estimate'}
                </Text>
              </div>
            )}

            <Text variant={TypographyStyles.H3}>
              Have you submitted your estimated quarterly tax payment?
            </Text>

            <div className="flex flex-row gap-2 mt-2 w-full">
              <FormikRadioToggleButton
                style={{ width: '100px' }}
                name="paymentSubmitted"
                value="yes"
              >
                Yes
              </FormikRadioToggleButton>

              <FormikRadioToggleButton
                style={{ width: '100px' }}
                name="paymentSubmitted"
                value="no"
              >
                No
              </FormikRadioToggleButton>
            </div>

            {!isNil(paymentSubmitted) &&
              paymentSubmitted === 'no' &&
              quarter === '4' && (
                <Text
                  variant={TypographyStyles.BODY_SM}
                  className="text-muted-foreground"
                >
                  Not paying estimated taxes may trigger underpayment penalties
                </Text>
              )}

            {!isNil(paymentSubmitted) && paymentSubmitted === 'yes' && (
              <>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Form.Input
                    required
                    name={getFieldName<typeof formik.values>(
                      'paymentAmountInDollars'
                    )}
                    label="How much did you pay?"
                    componentType="currency"
                    allowNegativeValue={false}
                    placeholder="$0.00"
                  />

                  <Form.DateInput
                    required
                    name={getFieldName<typeof formik.values>('paymentDate')}
                    label="When did you pay?"
                    maxDate={new Date()}
                    placeholder="Enter date"
                    fullWidth
                  />
                </div>

                <div className="space-y-2">
                  <Text variant={TypographyStyles.H3}>Attach Receipt</Text>

                  <Text variant={TypographyStyles.BODY_SM}>
                    Please attach the receipt for your payment. This helps us
                    with your bookkeeping.
                  </Text>

                  {uploadedFile?.title && (
                    <div className="mt-2 space-y-1">
                      <Link href={uploadedFile.url} newPage>
                        {uploadedFile.title}
                      </Link>
                      <Text variant={TypographyStyles.BODY_SM}>
                        <i>Can be found in your &quot;Tax&quot; Documents</i>
                      </Text>
                    </div>
                  )}

                  <DocumentUploadDialog
                    hasTrigger
                    onUpload={uploadDocuments}
                    isUploadDisabled={isUploading}
                    cardTitle="Upload Tax Payment Receipt"
                    cardDescription={`Please upload the receipt for your Q${quarter} ${year} tax payment.`}
                  />
                </div>
              </>
            )}
          </div>
        </FormikProvider>

        <Dialog.Footer className="pt-6">
          <Dialog.Close asChild>
            <Button className="w-[121px]" variant="secondary">
              Cancel
            </Button>
          </Dialog.Close>

          <Button
            disabled={isNil(paymentSubmitted) || loading}
            loading={loading}
            onClick={formik.submitForm}
          >
            Update
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}
